/**
 * Internationally-recognized SEO scoring standards.
 *
 * Everything here maps to public benchmarks so tenants, exports, and future
 * integrations (BI dashboards, ticketing systems) speak the same language as
 * Lighthouse, Google web.dev Core Web Vitals, and the standard keyword-tool
 * difficulty conventions used by Ahrefs / Moz / SEMrush.
 *
 * Import into workers, API responses, and the web app. Shared source of truth
 * for tier labels, colour bands, and cutoff constants.
 */

// ─── Site health score tiers (Lighthouse convention) ────────────────────
// https://developer.chrome.com/docs/lighthouse/performance/performance-scoring
// Lighthouse buckets its 0-100 category scores into three publicly-known bands:
//   90-100 → Good        (green)
//   50-89  → Needs Improvement (orange)
//   0-49   → Poor        (red)
// We mirror those cutoffs so a tenant seeing "Needs Improvement" in this app
// gets the same signal as when they run PageSpeed Insights.

export type HealthTier = 'good' | 'needs_improvement' | 'poor';

export interface TierBand {
  tier: HealthTier;
  label: string;
  /** Tailwind-safe colour tokens; UI can map these to its palette. */
  colorToken: 'success' | 'warning' | 'destructive';
}

export const HEALTH_SCORE_CUTOFFS = {
  goodMin: 90,
  needsImprovementMin: 50,
} as const;

export function healthTier(score: number): TierBand {
  if (score >= HEALTH_SCORE_CUTOFFS.goodMin) {
    return { tier: 'good', label: 'Good', colorToken: 'success' };
  }
  if (score >= HEALTH_SCORE_CUTOFFS.needsImprovementMin) {
    return { tier: 'needs_improvement', label: 'Needs Improvement', colorToken: 'warning' };
  }
  return { tier: 'poor', label: 'Poor', colorToken: 'destructive' };
}

// ─── Core Web Vitals thresholds (Google web.dev standard) ──────────────
// https://web.dev/articles/vitals
// These are the exact cutoffs Google uses for the CWV assessment on Search
// Console's page-experience report. Do not change without updating web.dev.

export const CWV_THRESHOLDS = {
  /** Largest Contentful Paint (milliseconds). */
  lcp: { good: 2500, needsImprovement: 4000 },
  /** Interaction to Next Paint (milliseconds). Replaced FID in 2024. */
  inp: { good: 200, needsImprovement: 500 },
  /** Cumulative Layout Shift (unitless score). */
  cls: { good: 0.1, needsImprovement: 0.25 },
  /** First Contentful Paint (milliseconds). */
  fcp: { good: 1800, needsImprovement: 3000 },
  /** Time to First Byte (milliseconds). */
  ttfb: { good: 800, needsImprovement: 1800 },
} as const;

export type CwvMetric = keyof typeof CWV_THRESHOLDS;

export function cwvRating(metric: CwvMetric, value: number): HealthTier {
  const t = CWV_THRESHOLDS[metric];
  if (value <= t.good) return 'good';
  if (value <= t.needsImprovement) return 'needs_improvement';
  return 'poor';
}

// ─── Keyword difficulty tiers (Ahrefs convention) ──────────────────────
// Ahrefs, Moz, and SEMrush all publish a 0-100 KD score with the same
// commonly-cited tier bands. We match those cutoffs so users cross-referencing
// against another tool see the same tier label.
//   0-10  → Easy
//   11-30 → Medium
//   31-70 → Hard
//   71-100 → Super Hard

export type KeywordDifficultyTier = 'easy' | 'medium' | 'hard' | 'super_hard';

export const KEYWORD_DIFFICULTY_CUTOFFS = {
  easyMax: 10,
  mediumMax: 30,
  hardMax: 70,
} as const;

export function keywordDifficultyTier(difficulty: number): {
  tier: KeywordDifficultyTier;
  label: string;
} {
  if (difficulty <= KEYWORD_DIFFICULTY_CUTOFFS.easyMax) {
    return { tier: 'easy', label: 'Easy' };
  }
  if (difficulty <= KEYWORD_DIFFICULTY_CUTOFFS.mediumMax) {
    return { tier: 'medium', label: 'Medium' };
  }
  if (difficulty <= KEYWORD_DIFFICULTY_CUTOFFS.hardMax) {
    return { tier: 'hard', label: 'Hard' };
  }
  return { tier: 'super_hard', label: 'Super Hard' };
}

// ─── Search rank / position tiers ─────────────────────────────────────
// Widely-used SEO convention:
//   1-3   → Top 3 (huge CTR — Google's own reports show ~40%/18%/10% at
//                  positions 1/2/3)
//   4-10  → Page 1
//   11-20 → Page 2
//   21+   → Below the fold (essentially invisible for most queries)

export type SearchPositionTier = 'top3' | 'page1' | 'page2' | 'below';

export function searchPositionTier(position: number): {
  tier: SearchPositionTier;
  label: string;
} {
  if (position >= 1 && position <= 3) return { tier: 'top3', label: 'Top 3' };
  if (position <= 10) return { tier: 'page1', label: 'Page 1' };
  if (position <= 20) return { tier: 'page2', label: 'Page 2' };
  return { tier: 'below', label: 'Below page 2' };
}

// ─── Impact score tiers (0-100 shared convention) ─────────────────────
// Used for audit issue impact hints and competitor content gaps. Sticks to
// the same quartile split most SEO tools use so users can eyeball severity
// without learning our vocabulary.

export type ImpactTier = 'low' | 'medium' | 'high' | 'critical';

export function impactTier(score: number): { tier: ImpactTier; label: string } {
  if (score <= 25) return { tier: 'low', label: 'Low' };
  if (score <= 50) return { tier: 'medium', label: 'Medium' };
  if (score <= 75) return { tier: 'high', label: 'High' };
  return { tier: 'critical', label: 'Critical' };
}
